import {
  AccessibleOutlined,
  HomeOutlined,
  LocalHospitalOutlined,
  Person,
  Phone,
  WcOutlined,
} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../common/button/commonButton';
import MessageDialog from '../../common/dialog/commonDialogBox';
import { APP_LINK } from '../../common/menu/menuItems/menuList';
import ReusableFormFields from '../../common/textfield/ReusableTextfield';
import { styles } from '../../commonStyle/formStyles';
import { fetchBlocks } from '../../redux/block/blockAction';
import { fetchDepartments } from '../../redux/department/departmentAction';
import { fetchFloors } from '../../redux/floor/floorAction';
import {
  addPatient,
  resetCurrentPatient,
} from '../../redux/patient/patientAction';
import { dataPatient } from '../../redux/value/storeAction';
import { fetchWards } from '../../redux/ward/wardAction';

function PatientForm() {
  const [numberOfAttenders, setNumberOfAttenders] = useState(0);
  const [currentPage, setcurrentPage] = useState(1);
  const [messageDialog, setMessageDialog] = useState({
    open: false,
    title: 'Notification',
    message: '',
    type: 'success',
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({
    shouldUnregister: false,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const departmentList = useSelector(
    (state) => state?.department?.fetchDepartments || {}
  );
  const patientState = useSelector(
    (state) => state?.patients?.addPatient || {}
  );
  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const watchBlock = watch('block');
  const watchWard = watch('ward');
  const watchFloor = watch('floor');
  const patientData = patientState?.data?.MemberInfo;
  
  useEffect(() => {
    if (patientState && patientState.success) {
      dispatch(dataPatient(patientData));
    }
  }, [dispatch, patientState]);
  
  const user = useSelector((state) => state?.values?.patientData || {});

  useEffect(() => {
    if (watchBlock) {
      localStorage.setItem('block', watchBlock);
    }
    if (watchWard) {
      localStorage.setItem('ward', watchWard);
    }
    if (watchFloor) {
      localStorage.setItem('floor', watchFloor);
    }
  }, [watchBlock, watchWard, watchFloor]);

  useEffect(() => {
    dispatch(fetchDepartments());
    dispatch(fetchBlocks());
  }, [dispatch]);

  useEffect(() => {
    const blockId = watchBlock;
    if (blockId) {
      dispatch(fetchFloors(blockId, currentPage));
    }
  }, [watchBlock, dispatch, currentPage]);

  useEffect(() => {
    if (watchBlock && watchFloor) {
      dispatch(fetchWards(watchBlock, watchFloor, currentPage));
    }
  }, [watchBlock, watchFloor, dispatch, currentPage]);

  useEffect(() => {
    if (patientState.success) {
      reset();
      setMessageDialog({
        open: true,
        title: 'Patient Registration',
        message: 'Patient updated successfully!',
        type: 'success',
      });
      dispatch(resetCurrentPatient());
      navigate(APP_LINK.ADDADMISSIONDETAILS);
    } else if (patientState.error) {
      if (patientState.errorFields) {
        Object.entries(patientState.errorFields).forEach(([field, message]) => {
          setError(field, {
            type: 'server',
            message: message,
          });
        });
      } else if (
        typeof patientState.error === 'string' &&
        patientState.error.includes('phone must be unique')
      ) {
        setError('phone', {
          type: 'server',
          message: 'Phone number already exists in the system',
        });
      } else {
        const errorMessage =
          patientState.errorMessage ||
          patientState.error ||
          'An error occurred while submitting the form';
        setMessageDialog({
          open: true,
          title: 'Registration Error',
          message: errorMessage,
          type: 'error',
        });
      }
      dispatch(resetCurrentPatient());
    }
  }, [patientState, reset, setError, dispatch]);

  const inputFields = useMemo(
    () => [
      {
        name: 'ip_no',
        label: 'IP Number / ஐ.பி எண்',
        placeholder: 'Enter IP Number',
        type: 'text',
        tabIndex: 1,
        rules: { required: 'IP Number is required' },
        startIcon: <LocalHospitalOutlined />,
      },
      {
        name: 'patient_Name',
        label: 'Patient Name / நோயாளியின் பெயர்',
        placeholder: 'Enter patient name',
        type: 'text',
        tabIndex: 2,
        rules: { required: 'Patient Name is required' },
        startIcon: <Person />,
      },
      {
        name: 'phone',
        label: 'Phone Number / தொலைபேசி எண்',
        placeholder: 'Enter phone number',
        type: 'tel',
        tabIndex: 3,
        maxLength: 10,
        showCharCount: false,
        rules: {
          // required: 'Phone number is required',
          pattern: {
            value: /^(6|7|8|9)\d{9}$/,
            message: 'Invalid phone number',
          },
          maxLength: {
            value: 10,
            message: 'Phone number should be 10 digits',
          },
        },
        startIcon: <Phone />,
      },
      {
        name: 'address',
        label: 'Address / முகவரி',
        placeholder: 'Enter address',
        type: 'text',
        tabIndex: 4,
        rules: { required: 'Address is required' },
        startIcon: <HomeOutlined />,
        multipleLine: true,
      },
      
      {
        name: 'gender',
        label: 'Gender / பாலினம்',    
        placeholder: 'Select gender',
        type: 'select',
        tabIndex: 5,
        options: [
          { value: 'Male', label: 'Male / ஆண்' },
          { value: 'Female', label: 'Female / பெண்' },
          { value: 'Other', label: 'Other / மற்றவை' },
        ],
        rules: { required: 'Gender is required' },
        startIcon: <WcOutlined />,

        onKeyDown: (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }
      },
      {
        name: 'age',
        label: 'Age / வயது',
        tabIndex: 6,
        placeholder: 'Enter age',
        type: 'text',
        maxLength: 3,
        showCharCount: false,
        rules: {
          required: 'Age is required',
          pattern: {
            value: /^[0-9]+$/,
            message: 'Please enter a valid age (numbers only)',
          },
        },
        startIcon: <AccessibleOutlined />,
      },
    ],
    [departmentList, blockList, floorList, wardList]
  );

  const watchAttenderType = watch('attender_type');

  useEffect(() => {
    if (watchAttenderType !== '1') {
      setNumberOfAttenders(0);
    }
  }, [watchAttenderType]);


  const onSubmit = (data) => {
    clearErrors();

    const patientData = {
      patient_name: data.patient_Name,
      ip_number: data.ip_no,
      phone: data.phone,
      address: data.address || '',
      attender_type: data.attender_type || '1',
      number_attenders: numberOfAttenders.toString() || '1',
      gender_type:
        data.gender === 'Male' ? 'M' : data.gender === 'Female' ? 'F' : 'O',
      status: '1',
      age: data.age,
    };

    dispatch(addPatient(patientData));
  };

  const handleCancel = () => {
    reset();
    clearErrors();
    setNumberOfAttenders(0);
  };

  const handleBack = () => {
    navigate(APP_LINK.LISTALLPATIENT);
  };

  const handleDialogClose = () => {
    setMessageDialog({ ...messageDialog, open: false });
    // if (messageDialog.type === 'success') {
    //   navigate(APP_LINK.ADDADMISSIONDETAILS);
    // }
  };

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper} elevation={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={8} lg={9} xl={11}>
            <Typography variant="h3" sx={styles.heading}>
              In-Patient Registration
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={4} lg={3} xl={1} mb={2}>
            <Button
              variant="contained"
              color="error"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </Grid>
        </Grid>

        <form onSubmit={handleSubmit(onSubmit)} >
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <ReusableFormFields
                inputFields={inputFields}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                gridProps={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 6 }}
              />
            </Grid>
          </Grid>
          <Box sx={styles.buttonsContainer}>
            <CustomButton
              type="button"
              text="Cancel"
              variant="contained"
              disableElevation
              size="small"
              onClick={handleCancel}
              tabIndex={8}
            />
            <CustomButton
              type="submit"
              text="Submit"
              variant="contained"
              disableElevation
              size="small"
              tabIndex={7}
            />
          </Box>
        </form>
      </Paper>
      <MessageDialog
        open={messageDialog.open}
        onClose={handleDialogClose}
        title={messageDialog.title}
        message={messageDialog.message}
        type={messageDialog.type}
      />
    </Box>
  );
}

export default PatientForm;